import Image from "next/image"
import { cn } from "@/lib/utils"
import { track } from "@/lib/track"
import { useEffect, useState, useLayoutEffect, useCallback, useRef } from 'react';
import { TypingEffect } from "@/components/site/sections/widgets/TypingEffect"
import { useRouter } from "next/navigation"
import dynamic from 'next/dynamic';
import { useCookie } from "@/hooks/useCookie";

// 动态导入依赖cookie状态的组件
const PhoneNumberInput = dynamic(() => import('./widgets/PhoneNumberInput'), {
  ssr: false, // 禁止服务端渲染
  loading: () => (
    <div className="h-[52px] w-[300px] rounded-full bg-gray-200 animate-pulse"></div>
  )
});

const HeroButton = dynamic(() => import('./widgets/HeroButton'), {
  ssr: false,
  loading: () => (
    <div className="h-[52px] w-[170px] rounded-full bg-gray-200 animate-pulse"></div>
  )
});


export const HeroSection = () => {
  const router = useRouter();
  const [phoneNumber, setPhoneNumber] = useState('');
  const cookieHasNickname = !!useCookie('nickname');
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  const revealTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isClient, setIsClient] = useState(false); // 新增状态标记客户端

  // 防抖的reveal函数
  const reveal = useCallback(() => {
    if (revealTimeoutRef.current) {
      clearTimeout(revealTimeoutRef.current);
    }

    revealTimeoutRef.current = setTimeout(() => {
      const reveals = document.querySelectorAll("[data-reveal]");
      const windowHeight = window.innerHeight;

      reveals.forEach((element) => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 10;

        if (elementTop < windowHeight - elementVisible) {
          element.setAttribute('data-reveal', 'true');
        } else {
          element.setAttribute('data-reveal', 'false');
        }
      });
    }, 100); // 100ms防抖
  }, []);

  // 使用useLayoutEffect处理DOM操作
  useLayoutEffect(() => {
    // 初始检查
    reveal();

    // 添加防抖的滚动监听
    scrollHandlerRef.current = reveal;
    window.addEventListener("scroll", scrollHandlerRef.current);

    return () => {
      if (scrollHandlerRef.current) {
        window.removeEventListener("scroll", scrollHandlerRef.current);
      }
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current);
      }
    };
  }, [reveal]);

  // 延迟非关键的状态更新
  useEffect(() => {
    setIsClient(true); // 标记客户端已加载
  }, []);

  return (
    <section className="space-y-6" suppressHydrationWarning>
      <div className="container flex max-w-[64rem] flex-col items-center text-center">
        <div
          data-reveal="false"
          className={cn(
            "flex flex-col flex-1",
            "relative transition-all duration-500",
            "translate-y-5 opacity-0",
            "data-[reveal=true]:translate-y-0 data-[reveal=true]:opacity-100",
          )}
        >
          <h2
            className={cn(
              "flex-1 font-heading font-semibold",
              "md:text-[56px] text-[64px]",
              "md:mt-[122px] mt-[178px]",
            )}>
            AI 全面提速的&nbsp;&nbsp;
            <span className="sr-only">
              文献筛选、论文阅读、笔记摘录、主题分类、文献综述、学术写作
            </span>
            <TypingEffect
              strings={['文献综述', '文献筛选', '论文阅读', '文献翻译', '笔记摘录', '主题分类', '学术写作']}
            />
          </h2>
          <p
            className={cn(
              "flex-1 leading-normal text-muted-foreground sm:leading-8 mt-3",
              "md:text-[20px] text-[24px]",
            )}>
            从阅读、翻译、笔记到写作的全新工作流，Findin AI 10倍速你的学术研究效率。
          </p>
        </div>
        <div
          data-reveal="false"
          className={cn(
            "space-x-3 md:mt-[38px] mt-[46px]",
            "relative transition-all duration-500 delay-400",
            "translate-y-5 opacity-0",
            "data-[reveal=true]:translate-y-0 data-[reveal=true]:opacity-100",
            "flex"
          )}
        >
          {
            isClient && !cookieHasNickname && (
              <PhoneNumberInput
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
              />
            )
          }
          <HeroButton
            cookieHasNickname={cookieHasNickname}
            onClick={() => {
              track('landing_page_quick_start');
              router.push(
                cookieHasNickname ? '/papers' : (
                  phoneNumber ? `/login?phone=${phoneNumber}` : '/login'
                )
              );
            }}
          />
        </div>
        <div className="relative h-[450px] md:h-[400px] w-full mt-6 mb-20">
          <div
            data-reveal="false"
            className={cn(
              "absolute -translate-x-1/2 -top-3 left-1/2",
              "w-[82.8px] h-[192px]",
              "md:w-[69px] md:h-[160px]",
              "transition-all translate-y-5 opacity-0 duration-1500 delay-1700",
              "data-[reveal=true]:animate-shake-hand",
              "data-[reveal=true]:translate-y-0 data-[reveal=true]:opacity-100",
            )}
          >
            <Image
              priority
              className="w-full h-full"
              src="/images/section-1-hand.png"
              width={82.8}
              height={192}
              alt="Hand icon"
              // 添加模糊占位符
              placeholder="blur"
              blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAFAAUDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAeEAABBAIDAQAAAAAAAAAAAAABAAIDBAUREiExkf/EABUBAQEAAAAAAAAAAAAAAAAAAAID/8QAGBEAAwEBAAAAAAAAAAAAAAAAAAERAjH/2gAMAwEAAhEDEQA/AJbF2zXrQxQ2JY2NYAGteQB0lKXZt2K8McNiWONrQA1ryAOvSUpR1VeiT//Z"
            />
          </div>
          <div
            data-reveal="false"
            className={cn(
              "absolute left-1/2",
              "w-[246.5px] h-[84.23px] translate-x-[112px] top-[52px]",
              "md:w-[205.41px] md:h-[70.19px] md:top-10",
              "transition-all duration-600 delay-2667",
              "translate-y-12 opacity-0",
              "data-[reveal=true]:animate-pop-bubble",
              "data-[reveal=true]:translate-y-0 data-[reveal=true]:opacity-100",
            )}
          >
            <Image
              priority={false} // 非LCP图片，不设置priority
              className="w-full h-full"
              src="/images/section-1-pop.png"
              width={246.5}
              height={84.23}
              alt="Pop bubble"
              loading="eager"
            />
          </div>
          <div
            data-reveal="false"
            className={cn(
              "absolute -translate-x-1/2 left-1/2",
              "w-[734.4px] h-[380.4px] top-[72px]",
              "md:w-[679.32px] md:h-[351.87px] md:top-12",
              "transition-all duration-1000 delay-700",
              "translate-y-5 opacity-0",
              "data-[reveal=true]:translate-y-0 data-[reveal=true]:opacity-100",
            )}
          >
            <Image
              priority // LCP图片，设置priority
              className="w-full h-full"
              src="/images/section-1-papers.png"
              width={734.4}
              height={380.4}
              alt="Papers showcase"
              // 关键优化：添加模糊占位符和尺寸提示
              placeholder="blur"
              blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAFAAUDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAeEAABBAIDAQAAAAAAAAAAAAABAAIDBAUREiExkf/EABUBAQEAAAAAAAAAAAAAAAAAAAID/8QAGBEAAwEBAAAAAAAAAAAAAAAAAAERAjH/2gAMAwEAAhEDEQA/AJbF2zXrQxQ2JY2NYAGteQB0lKXZt2K8McNiWONrQA1ryAOvSUpR1VeiT//Z"
              // 添加尺寸提示帮助浏览器预留空间
              sizes="(max-width: 768px) 679px, 734px"
            />
          </div>
        </div>
      </div>
    </section>
  )
}